import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Chip,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  LinearProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ContentCopy as ContentCopyIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Medication as MedicationIcon,
  Science as ScienceIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import documentService, { DocumentStatus, DocumentResult } from '../services/documentService';
import { format } from 'date-fns';

interface ExtractedField {
  value: string;
  confidence: number;
}

interface ExtractedData {
  raw_ocr_text?: string;
  text_length?: number;
  word_count?: number;
  line_count?: number;
  patient?: {
    name?: ExtractedField;
    mrn?: ExtractedField;
    dob?: ExtractedField;
    age?: ExtractedField;
    sex?: ExtractedField;
    contact?: ExtractedField;
    email?: ExtractedField;
    address?: ExtractedField;
  };
  provider?: {
    name?: ExtractedField;
    npi?: ExtractedField;
    facility?: ExtractedField;
  };
  dates?: {
    visit_date?: ExtractedField;
  };
  clinical?: {
    chief_complaint?: ExtractedField;
    diagnosis?: ExtractedField;
  };
  prescription?: {
    medications?: Array<{ name: string; dosage: string; confidence: number }>;
    pharmacy?: ExtractedField;
  };
  lab_results?: Array<{ test: string; value: string; unit: string; confidence: number }>;
  document_type?: string;
}

const DocumentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<DocumentStatus | null>(null);
  const [results, setResults] = useState<DocumentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchStatus = async () => {
      try {
        const data = await documentService.getDocumentStatus(id);
        setStatus(data);
        
        if (data.status === 'completed') {
          const resultsData = await documentService.getDocumentResults(id);
          setResults(resultsData);
        }
      } catch (err: any) {
        setError(err.message || 'Error loading document');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();

    const interval = setInterval(() => {
      if (status?.status === 'processing' || status?.status === 'pending') {
        fetchStatus();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [id, status?.status]);

  const getStatusColor = (statusStr: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (statusStr) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 90) return '#4caf50'; // Green
    if (confidence >= 75) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const getConfidenceLabel = (confidence: number): string => {
    if (confidence >= 90) return 'High';
    if (confidence >= 75) return 'Medium';
    return 'Low';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const renderConfidenceBadge = (confidence: number) => (
    <Chip
      label={`${confidence.toFixed(0)}% ${getConfidenceLabel(confidence)}`}
      size="small"
      sx={{
        backgroundColor: getConfidenceColor(confidence),
        color: 'white',
        fontWeight: 'bold',
        fontSize: '0.7rem',
      }}
    />
  );

  const renderField = (label: string, field?: ExtractedField, icon?: React.ReactNode) => {
    if (!field || !field.value || field.value === 'Not Found') {
      return null;
    }

    return (
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {icon && <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.7rem' }}>
            {label}
          </Typography>
          {renderConfidenceBadge(field.confidence)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" sx={{ flex: 1, fontWeight: 500 }}>
            {field.value}
          </Typography>
          <Tooltip title="Copy to clipboard">
            <IconButton size="small" onClick={() => copyToClipboard(field.value)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress
          variant="determinate"
          value={field.confidence}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: '#e0e0e0',
            mt: 0.5,
            '& .MuiLinearProgress-bar': {
              backgroundColor: getConfidenceColor(field.confidence),
              borderRadius: 3,
            },
          }}
        />
      </Box>
    );
  };

  const extractedData = results?.extracted_data as ExtractedData | undefined;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }}>Loading document...</Typography>
      </Container>
    );
  }

  if (error || !status) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Document not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/documents')}>
          Back to Documents
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/documents')} variant="outlined">
            Back
          </Button>
          <Box>
            <Typography variant="h4">Document Details</Typography>
            {status.status === 'completed' && results && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip label={results.document_type} color="primary" size="small" />
                {renderConfidenceBadge((results.confidence_score * 100))}
                {results.requires_manual_review && (
                  <Chip label="Needs Review" color="warning" icon={<WarningIcon />} size="small" />
                )}
              </Stack>
            )}
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </Box>

      {/* Status Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Chip label={status.status.toUpperCase()} color={getStatusColor(status.status)} sx={{ mt: 1 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary">Progress</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LinearProgress variant="determinate" value={status.progress} sx={{ flexGrow: 1, height: 8, borderRadius: 1 }} />
              <Typography variant="body2" fontWeight="bold">{status.progress}%</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary">Job ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
              {status.job_id.substring(0, 12)}...
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary">Created</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {format(new Date(status.created_at), 'MMM dd, yyyy HH:mm')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Processing Status */}
      {(status.status === 'processing' || status.status === 'pending') && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<CircularProgress size={20} />}>
          Document is being processed... Checking status every 3 seconds.
        </Alert>
      )}

      {/* Failed Status */}
      {status.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Processing failed: {status.message || 'Unknown error'}
        </Alert>
      )}

      {/* Completed Status - Enhanced Display */}
      {status.status === 'completed' && results && extractedData && (
        <>
          {/* Copy Success Snackbar */}
          {copySuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Copied to clipboard!
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Patient Information */}
            {extractedData.patient && Object.values(extractedData.patient).some(v => v?.value && v.value !== 'Not Found') && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6">Patient Information</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {renderField('Full Name', extractedData.patient.name)}
                    {renderField('Patient ID / MRN', extractedData.patient.mrn)}
                    {renderField('Date of Birth', extractedData.patient.dob)}
                    {renderField('Age', extractedData.patient.age)}
                    {renderField('Sex', extractedData.patient.sex)}
                    {renderField('Contact', extractedData.patient.contact)}
                    {renderField('Email', extractedData.patient.email)}
                    {renderField('Address', extractedData.patient.address)}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Provider Information */}
            {extractedData.provider && Object.values(extractedData.provider).some(v => v?.value && v.value !== 'Not Found') && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <LocalHospitalIcon color="primary" />
                      <Typography variant="h6">Provider Information</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {renderField('Provider Name', extractedData.provider.name)}
                    {renderField('NPI Number', extractedData.provider.npi)}
                    {renderField('Facility', extractedData.provider.facility)}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Dates */}
            {extractedData.dates && Object.values(extractedData.dates).some(v => v?.value) && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CalendarIcon color="primary" />
                      <Typography variant="h6">Dates</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {renderField('Visit Date', extractedData.dates.visit_date)}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Clinical Information */}
            {extractedData.clinical && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <DescriptionIcon color="primary" />
                      <Typography variant="h6">Clinical Information</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {renderField('Chief Complaint', extractedData.clinical.chief_complaint)}
                    {renderField('Diagnosis', extractedData.clinical.diagnosis)}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Prescription */}
            {extractedData.prescription && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <MedicationIcon color="primary" />
                      <Typography variant="h6">Prescription Information</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {extractedData.prescription.medications && extractedData.prescription.medications.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Medications
                        </Typography>
                        <Grid container spacing={2}>
                          {extractedData.prescription.medications.map((med, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                                  <Box>
                                    <Typography variant="body1" fontWeight="bold">
                                      {med.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {med.dosage}
                                    </Typography>
                                  </Box>
                                  {renderConfidenceBadge(med.confidence)}
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                    {renderField('Pharmacy', extractedData.prescription.pharmacy)}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Lab Results */}
            {extractedData.lab_results && extractedData.lab_results.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <ScienceIcon color="primary" />
                      <Typography variant="h6">Lab Results</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {extractedData.lab_results.map((result, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                            <Typography variant="caption" color="text.secondary">
                              {result.test}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Typography variant="h6">
                                {result.value} <Typography component="span" variant="body2">{result.unit}</Typography>
                              </Typography>
                              {renderConfidenceBadge(result.confidence)}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Document Statistics */}
            {extractedData && (extractedData.text_length || extractedData.word_count) && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <AssessmentIcon color="primary" />
                      <Typography variant="h6">Document Statistics</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {extractedData.text_length && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography color="text.secondary">Characters:</Typography>
                        <Typography fontWeight="bold">{extractedData.text_length.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {extractedData.word_count && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography color="text.secondary">Words:</Typography>
                        <Typography fontWeight="bold">{extractedData.word_count.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {extractedData.line_count && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary">Lines:</Typography>
                        <Typography fontWeight="bold">{extractedData.line_count.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {results.processing_time && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                        <Typography color="text.secondary">Processing Time:</Typography>
                        <Typography fontWeight="bold">{results.processing_time.toFixed(2)}s</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Raw OCR Text */}
            {extractedData.raw_ocr_text && (
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Raw OCR Text</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        backgroundColor: '#1e1e1e',
                        color: '#d4d4d4',
                        p: 3,
                        borderRadius: 1,
                        maxHeight: 500,
                        overflow: 'auto',
                        fontFamily: '"Fira Code", "Courier New", monospace',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        lineHeight: 1.8,
                      }}
                    >
                      {extractedData.raw_ocr_text}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copyToClipboard(extractedData.raw_ocr_text || '')}
                      >
                        Copy Full Text
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        {extractedData.text_length?.toLocaleString()} characters
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {/* Validation Results */}
            {results.validation_results && results.validation_results.length > 0 && (
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Validation Results</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {results.validation_results.map((validation: any, index: number) => (
                      <Alert
                        key={index}
                        severity={validation.passed ? 'success' : (validation.severity as any)}
                        icon={validation.passed ? <CheckCircleIcon /> : <ErrorIcon />}
                        sx={{ mb: 1 }}
                      >
                        <strong>{validation.rule_id}:</strong> {validation.message}
                        {validation.field && <Typography variant="caption" display="block"> Field: {validation.field}</Typography>}
                      </Alert>
                    ))}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default DocumentView;